import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { inviteApi } from '@/services/api'
import { BookOpen, Building2, CheckCircle, AlertCircle } from 'lucide-react'

interface InviteInfo {
  client_name: string
  firm_name: string
  email: string
  user_exists: boolean
}

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const login = useAuthStore((s) => s.login)

  const [info, setInfo] = useState<InviteInfo | null>(null)
  const [loadError, setLoadError] = useState('')
  const [loading, setLoading] = useState(true)

  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!token) return
    inviteApi.getInfo(token)
      .then((res) => setInfo(res.data))
      .catch(() => setLoadError('This invite link is invalid or has expired.'))
      .finally(() => setLoading(false))
  }, [token])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!token) return
    setError('')
    setSubmitting(true)
    try {
      const res = await inviteApi.accept(token, {
        password,
        ...(info?.user_exists ? {} : { full_name: fullName }),
      })
      const { access, refresh, client_id } = res.data
      login(access, refresh)
      navigate(`/portal/${client_id}/reports`, { replace: true })
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Something went wrong'
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center">
        <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-surface-50 flex items-center justify-center px-4">
        <div className="card p-10 max-w-md w-full text-center">
          <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Invite not found</h2>
          <p className="text-sm text-gray-500 mb-6">{loadError}</p>
          <Link to="/login" className="btn-primary inline-flex">Back to login</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center px-4">
      <div className="card p-8 max-w-md w-full">
        {/* Logo */}
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-8 h-8 bg-brand-500 rounded-lg flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-white" />
          </div>
          <span className="text-base font-bold text-gray-900">LedgerPro</span>
        </div>

        {/* Invite context */}
        <div className="bg-brand-50 rounded-xl p-4 mb-6 flex items-start gap-3">
          <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
            <Building2 className="w-4 h-4 text-brand-500" />
          </div>
          <div>
            <p className="text-xs text-brand-600 font-medium">You've been invited to view</p>
            <p className="text-sm font-bold text-gray-900 mt-0.5">{info?.client_name}</p>
            <p className="text-xs text-gray-500 mt-0.5">by {info?.firm_name}</p>
          </div>
          <CheckCircle className="w-4 h-4 text-brand-500 flex-shrink-0 mt-0.5 ml-auto" />
        </div>

        <h2 className="text-xl font-bold text-gray-900 mb-1">
          {info?.user_exists ? 'Sign in to accept' : 'Create your account'}
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          {info?.user_exists
            ? `Enter your password for ${info.email} to accept the invitation.`
            : `Set up your account for ${info?.email} to access reports.`
          }
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email — always pre-filled and read-only */}
          <div>
            <label className="input-label">Email address</label>
            <input
              type="email"
              value={info?.email || ''}
              readOnly
              className="input bg-surface-50 text-gray-500 cursor-not-allowed"
            />
          </div>

          {/* Full name — only for new users */}
          {!info?.user_exists && (
            <div>
              <label className="input-label">Full name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="input"
                placeholder="Jane Smith"
                required
                autoFocus
              />
            </div>
          )}

          <div>
            <label className="input-label">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              placeholder={info?.user_exists ? 'Your password' : 'Min. 8 characters'}
              required
              minLength={info?.user_exists ? undefined : 8}
              autoFocus={info?.user_exists}
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
          )}

          <button type="submit" disabled={submitting} className="btn-primary w-full py-2.5 mt-2">
            {submitting
              ? 'Please wait...'
              : info?.user_exists ? 'Sign in & Accept' : 'Create account & Accept'
            }
          </button>
        </form>

        <p className="text-xs text-center text-gray-400 mt-5">
          Already have an account?{' '}
          <Link to="/login" className="text-brand-500 hover:text-brand-600 font-medium">Sign in</Link>
        </p>
      </div>
    </div>
  )
}
